import { onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import type { AppError } from "@/shared/api";
import { useSessionStore } from "@/entities/session";
import { safeReturnPath } from "@/shared/lib";

import { persistLastEmail, readLastEmail } from "./lastEmail";
import { mapLoginError } from "./mapLoginError";
import { validateLoginFields } from "./validate";

export interface LoginFieldErrors {
  email: string | null;
  password: string | null;
}

/**
 * Стан і submit-flow форми login (spec §4.3–4.4). Вся логіка тут (валідація,
 * мапінг помилок, редірект) — компонент лише рендерить і керує фокусом.
 */
export function useLoginForm() {
  const { t } = useI18n();
  const session = useSessionStore();
  const router = useRouter();
  const route = useRoute();

  const email = ref("");
  const password = ref("");
  const rememberMe = ref(false);
  const submitting = ref(false);
  const formError = ref<string | null>(null);
  const fieldErrors = reactive<LoginFieldErrors>({ email: null, password: null });

  // Префіл lastEmail (§6): є збережений email → remember був відмічений.
  onMounted(() => {
    const last = readLastEmail();
    if (last) {
      email.value = last;
      rememberMe.value = true;
    }
  });

  // Field-помилки зникають при редагуванні (§4.3).
  watch(email, () => {
    fieldErrors.email = null;
  });
  watch(password, () => {
    fieldErrors.password = null;
  });

  function validate(): boolean {
    const res = validateLoginFields(email.value, password.value);
    fieldErrors.email = res.email ? t(res.email) : null;
    fieldErrors.password = res.password ? t(res.password) : null;
    return !res.email && !res.password;
  }

  async function submit(): Promise<void> {
    formError.value = null;
    if (!validate()) {
      return;
    }
    submitting.value = true;
    try {
      await session.login({
        email: email.value.trim(),
        password: password.value,
        remember_me: rememberMe.value,
      });
      persistLastEmail(email.value.trim(), rememberMe.value);
      // router.replace (не push) — back не повертає на /login після входу (§4.4)
      await router.replace(safeReturnPath(route.query.return));
    }
    catch (err) {
      // Глобальний response-interceptor (ADR-0010 D4) гарантує AppError; login-
      // сабміт несе skipAuthRedirect → 401 нормалізується без глобального редіректу.
      formError.value = t(mapLoginError(err as AppError));
    }
    finally {
      submitting.value = false;
    }
  }

  return {
    email,
    password,
    rememberMe,
    submitting,
    formError,
    fieldErrors,
    submit,
  };
}
